import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelL } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { INSTANT_BUY_URL } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary, wheel } from '@sorare/core/src/lib/glossary';

import { Auction } from 'components/transfers/Auction';
import { InstantBuy } from 'components/transfers/InstantBuy';

const Content = styled(Vertical).attrs({
  gap: 5,
})`
  padding: var(--triple-unit);
`;

const Bullet = styled(Horizontal).attrs({
  gap: 2,
})`
  > :first-child {
    flex-shrink: 0;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const HowToWinTicketDialog = ({ open, onClose }: Props) => {
  const { sport } = useSportContext();
  return (
    <Dialog
      open={open}
      maxWidth="xs"
      onClose={onClose}
      title={
        <LabelL bold>
          <FormattedMessage {...wheel.howToWin} />
        </LabelL>
      }
    >
      <Content>
        <Vertical gap={2}>
          <Bullet>
            <InstantBuy size={3} color="var(--c-white)" />
            <BodyM color="var(--c-nd-600)" as="p">
              <FormattedMessage
                id="marketplace.wheel.tickets.howToWin.instantBuy"
                defaultMessage="Buy a Sorare 26 Card through Instant Buy to win 2 tickets"
              />
            </BodyM>
          </Bullet>
          <Bullet>
            <Auction size={3} color="var(--c-white)" />
            <BodyM color="var(--c-nd-600)" as="p">
              <FormattedMessage
                id="marketplace.wheel.tickets.howToWin.auction"
                defaultMessage="Buy a Sorare 26 Card through Auction to win 2 tickets"
              />
            </BodyM>
          </Bullet>
          {sport === Sport.FOOTBALL && (
            <Bullet>
              <Auction size={3} color="var(--c-white)" />
              <BodyM color="var(--c-nd-600)" as="p">
                <FormattedMessage
                  id="marketplace.wheel.tickets.howToWin.bidToEarn2"
                  defaultMessage="End a Sorare 26 Card Auction with the 2nd highest bid to win a ticket"
                />
              </BodyM>
            </Bullet>
          )}
        </Vertical>
        <BodyM color="var(--c-nd-600)" as="p">
          <FormattedMessage
            id="marketplace.wheel.tickets.howToWin.other"
            defaultMessage="Other ways of winning tickets may be added in the future — stay posted"
          />
        </BodyM>
        <Vertical>
          <Button
            size="large"
            fullWidth
            color="primary"
            to={INSTANT_BUY_URL[sport]}
          >
            <FormattedMessage
              id="marketplace.wheel.tickets.shopCards"
              defaultMessage="Shop Cards"
            />
          </Button>
          <Button size="large" fullWidth color="quaternary" onClick={onClose}>
            <FormattedMessage {...glossary.close} />
          </Button>
        </Vertical>
      </Content>
    </Dialog>
  );
};
